import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Button,
  Table,
  Form,
  InputGroup,
  Alert,
  Spinner,
  Modal,
  Pagination,
} from "react-bootstrap";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import LessonPlanManagementService, {
  LESSON_PLAN_TYPE,
  LessonPlanListItem,
  LessonPlanDetail,
} from "../../_services/LessonPlanManagementService";
import "./index.css";

const PAGE_SIZE = 20;

// TypeId → label. 39 = Lesson Plan, 29 = Learning Module (project rule).
function typeLabel(item: LessonPlanListItem): string {
  if (item.lessonPlanTypeId === LESSON_PLAN_TYPE.LEARNING_MODULE) return "Learning Module";
  if (item.lessonPlanTypeId === LESSON_PLAN_TYPE.LESSON_PLAN) return "Lesson Plan";
  return item.lessonPlanType === "learning_module" ? "Learning Module" : "Lesson Plan";
}

// Plain <span> pill — project rule forbids the Bootstrap Badge component.
function Pill({ text, tone }: { text: string; tone: "plan" | "module" | "muted" }) {
  const palette: Record<string, { bg: string; fg: string }> = {
    plan: { bg: "#e0edff", fg: "#1d4ed8" },
    module: { bg: "#e7f8ee", fg: "#15803d" },
    muted: { bg: "#eef1f4", fg: "#556" },
  };
  const c = palette[tone] || palette.muted;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  const s = String(d);
  const datePart = s.split("T")[0] || s;
  return datePart.split(" ")[0] || "—";
}

const LessonPlanManagement: React.FC = () => {
  useDocumentTitle("Lesson Plan Management");

  const [plans, setPlans] = useState<LessonPlanListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters (all applied client-side against the fetched list)
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<number | "">("");
  const [page, setPage] = useState(1);

  // View modal
  const [showView, setShowView] = useState(false);
  const [detail, setDetail] = useState<LessonPlanDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [viewingName, setViewingName] = useState("");

  const loadPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await LessonPlanManagementService.getLessonPlans({
        sortBy: "name",
        sortOrder: "asc",
      });
      setPlans(data);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load lesson plans. Please try again.",
      );
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  // Reset to first page whenever a filter changes.
  useEffect(() => {
    setPage(1);
  }, [search, typeFilter]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return plans.filter((p) => {
      if (typeFilter !== "" && p.lessonPlanTypeId !== typeFilter) return false;
      if (!term) return true;
      const hay = `${p.name || ""} ${p.code || ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [plans, search, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openView = async (item: LessonPlanListItem) => {
    setShowView(true);
    setViewingName(item.name);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const d = await LessonPlanManagementService.getLessonPlan(item.id);
      setDetail(d);
    } catch (e: any) {
      setDetailError(
        e?.response?.data?.message || e?.message || "Failed to load lesson plan detail.",
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const closeView = () => {
    setShowView(false);
    setDetail(null);
    setDetailError(null);
  };

  const totalSteps = (d: LessonPlanDetail | null): number =>
    (d?.groups || []).reduce((sum, g) => sum + (g.steps?.length || 0), 0);

  return (
    <div className="lesson-plan-management-page">
      <div className="lpm-header">
        <div>
          <h3 className="lpm-title">Lesson Plan Management</h3>
          <p className="lpm-subtitle">
            Search and view lesson plans &amp; learning modules.
          </p>
        </div>
        <Button variant="secondary" onClick={loadPlans} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <Card className="lpm-filter-card">
        <Card.Body className="lpm-filter-row">
          <InputGroup className="lpm-search">
            <Form.Control
              placeholder="Search by name or code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          <Form.Select
            className="lpm-type-select"
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(e.target.value === "" ? "" : Number(e.target.value))
            }
          >
            <option value="">All Types</option>
            <option value={LESSON_PLAN_TYPE.LESSON_PLAN}>Lesson Plan</option>
            <option value={LESSON_PLAN_TYPE.LEARNING_MODULE}>Learning Module</option>
          </Form.Select>
          <span className="lpm-count">
            <strong>{filtered.length}</strong> result{filtered.length === 1 ? "" : "s"}
          </span>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="lpm-alert">
          {error}
        </Alert>
      )}

      <Card className="lpm-table-card">
        <Card.Body>
          {loading ? (
            <div className="lpm-empty">
              <Spinner animation="border" size="sm" /> Loading lesson plans…
            </div>
          ) : pageItems.length === 0 ? (
            <div className="lpm-empty">No lesson plans found.</div>
          ) : (
            <Table hover responsive className="lpm-table">
              <thead>
                <tr>
                  <th style={{ width: "34%" }}>Name</th>
                  <th style={{ width: "16%" }}>Code</th>
                  <th style={{ width: "16%" }}>Type</th>
                  <th style={{ width: "10%" }}>Classes</th>
                  <th style={{ width: "14%" }}>Modified</th>
                  <th style={{ width: "10%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((p) => (
                  <tr key={p.id}>
                    <td title={p.name}>{p.name || "—"}</td>
                    <td>{p.code || "—"}</td>
                    <td>
                      <Pill
                        text={typeLabel(p)}
                        tone={
                          p.lessonPlanTypeId === LESSON_PLAN_TYPE.LEARNING_MODULE
                            ? "module"
                            : "plan"
                        }
                      />
                    </td>
                    <td>{p.numberOfClasses ?? "—"}</td>
                    <td>{fmtDate(p.lastModifiedDate)}</td>
                    <td>
                      <Button size="sm" variant="primary" onClick={() => openView(p)}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {totalPages > 1 && (
            <div className="lpm-pagination">
              <Pagination>
                <Pagination.Prev
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                />
                <Pagination.Item active>
                  {page} / {totalPages}
                </Pagination.Item>
                <Pagination.Next
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showView} onHide={closeView} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{viewingName || "Lesson Plan"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="lpm-empty">
              <Spinner animation="border" size="sm" /> Loading detail…
            </div>
          ) : detailError ? (
            <Alert variant="danger">{detailError}</Alert>
          ) : detail ? (
            <div className="lpm-detail">
              <div className="lpm-detail-meta">
                <div>
                  <span className="lpm-meta-label">Code</span>
                  <span className="lpm-meta-value">{detail.code || "—"}</span>
                </div>
                <div>
                  <span className="lpm-meta-label">Type</span>
                  <span className="lpm-meta-value">
                    {detail.lessonPlanType === "learning_module"
                      ? "Learning Module"
                      : "Lesson Plan"}
                  </span>
                </div>
                <div>
                  <span className="lpm-meta-label">Steps</span>
                  <span className="lpm-meta-value">{totalSteps(detail)}</span>
                </div>
                <div>
                  <span className="lpm-meta-label">Topics</span>
                  <span className="lpm-meta-value">
                    {detail.selectedTopics?.length ?? 0}
                  </span>
                </div>
                <div>
                  <span className="lpm-meta-label">Batches</span>
                  <span className="lpm-meta-value">
                    {detail.selectedBatches?.length ?? 0}
                  </span>
                </div>
              </div>

              {detail.remarks && (
                <p className="lpm-remarks">{detail.remarks}</p>
              )}

              <h6 className="lpm-section-title">Structure</h6>
              {(detail.groups || []).length === 0 ? (
                <div className="lpm-empty">No steps configured for this lesson plan.</div>
              ) : (
                detail.groups.map((g) => (
                  <div key={g.index} className="lpm-group">
                    <div
                      className="lpm-group-head"
                      style={{ borderLeftColor: g.color || "#2572EB" }}
                    >
                      {g.name}{" "}
                      <span className="lpm-group-count">
                        ({g.steps?.length || 0} step
                        {(g.steps?.length || 0) === 1 ? "" : "s"})
                      </span>
                    </div>
                    <ol className="lpm-steps">
                      {(g.steps || []).map((s) => (
                        <li key={s.id}>
                          <span className="lpm-step-name">{s.name}</span>
                          {s.resources && s.resources.length > 0 && (
                            <span className="lpm-step-res">
                              {s.resources.length} resource
                              {s.resources.length === 1 ? "" : "s"}
                            </span>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="lpm-empty">No detail available.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeView}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LessonPlanManagement;
